import { Mail, Phone, Globe } from "lucide-react";
import { ProfileAvatar } from "@/features/identity/components/ProfileAvatar";
import { PlatformIcon } from "@/features/identity/components/PlatformIcon";
import { getPlatform } from "@/features/identity/constants/platforms";
import { isSafeWebUrl } from "@/features/identity/validation";

const hostOf = (url) => {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
};

function ContactItem({ icon: Icon, label, value, href, testId }) {
  return (
    <div className="flex min-w-0 items-start gap-3 px-6 py-4 sm:px-8">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0">
        <p className="mw-eyebrow">{label}</p>
        <p data-testid={testId} className={`mt-1 truncate text-sm ${value ? "" : "text-muted-foreground"}`}>
          {value && href ? <a href={href} target="_blank" rel="noopener noreferrer" className="mw-link font-normal">{value}</a> : value || "Not added"}
        </p>
      </div>
    </div>
  );
}

export function IdentityPreview({ profile, links = [] }) {
  const visible = links.filter((l) => l.is_visible && isSafeWebUrl(l.url));
  const website = profile.website && isSafeWebUrl(profile.website) ? profile.website : null;
  const subtitle = [profile.professional_title, profile.company_name].filter(Boolean);
  return (
    <article data-testid="identity-preview" className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:p-8">
        <ProfileAvatar profile={profile} testId="preview-photo" />
        <div className="min-w-0 flex-1">
          <h2 data-testid="preview-full-name" className="font-serif text-3xl leading-tight tracking-tight sm:text-4xl">{profile.full_name || "Your name"}</h2>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            <span data-testid="preview-title">{profile.professional_title}</span>
            {subtitle.length > 1 && " · "}
            <span data-testid="preview-company">{profile.company_name}</span>
          </p>
        </div>
        {profile.company_logo_url && (
          <img src={profile.company_logo_url} alt={profile.company_name || "Company logo"} data-testid="preview-logo" className="h-12 max-w-[140px] self-start object-contain sm:self-center" />
        )}
      </div>
      <div className="grid border-t border-border sm:grid-cols-3">
        <ContactItem icon={Mail} label="Email" value={profile.email} href={profile.email && `mailto:${profile.email}`} testId="preview-email" />
        <ContactItem icon={Phone} label="Phone" value={profile.phone} href={profile.phone && `tel:${profile.phone.replace(/[^\d+]/g, "")}`} testId="preview-phone" />
        <ContactItem icon={Globe} label="Website" value={website && hostOf(website)} href={website} testId="preview-website" />
      </div>
      {profile.bio && (
        <p data-testid="preview-bio" className="whitespace-pre-line border-t border-border px-6 py-6 text-sm leading-relaxed text-foreground/85 sm:px-8 sm:text-base">{profile.bio}</p>
      )}
      <div className="border-t border-border px-6 py-5 sm:px-8">
        <p className="mw-eyebrow">Links</p>
        {visible.length ? (
          <ul className="mt-3 flex flex-wrap gap-2">
            {visible.map((l) => (
              <li key={l.id}>
                <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`preview-link-${getPlatform(l.platform).key}`} className="inline-flex min-h-[40px] items-center gap-2 rounded-md border border-border px-3 text-sm transition-colors hover:border-foreground">
                  <PlatformIcon platform={getPlatform(l.platform).key} />
                  {getPlatform(l.platform).label}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p data-testid="preview-no-links" className="mt-2 text-sm text-muted-foreground">No visible links.</p>
        )}
      </div>
    </article>
  );
}
